import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import { InlineQuestionEditor } from './InlineQuestionEditor';
import { CheckCircle2, Circle, TrendingUp, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { projectId } from '../utils/supabase/info';
import { 
  getAnsweredQuestions, 
  getConfigItemCompleteness,
  recalculateSOWCompleteness,
  type QuestionAnswer 
} from '../utils/questionCompletenessTracker';
import { WizardBanner } from './WizardBanner';
import { DynamicWizard } from './DynamicWizard';
import { WizardChoiceDialog } from './WizardChoiceDialog';
import { checkWizardAutoLaunch } from '../hooks/useWizardAutoLaunch';
import { WizardQuestionGroup } from './WizardQuestionGroup';
import { QuestionCard } from './QuestionCard';
import { SkipPlanBanner } from './SkipPlanBanner';
import { useSkipPlan } from '../hooks/useSkipPlan';
import {
  fetchQuestionMetadata,
  fetchWizardSessions,
  groupQuestionsByWizard,
  canEditInline,
  type Question,
  type WizardGroup
} from '../utils/wizardQuestionGrouping';

interface DiscoveryQuestionsPanelProps {
  configItemId: string;
  configItemName: string;
  companyId: string;
  sowId?: string;
  accessToken: string;
  isConsultant?: boolean;
  currentUser?: any;
  configItemCategory?: string;
  consultingFirmId?: string;
}

export function DiscoveryQuestionsPanel({
  configItemId,
  configItemName,
  companyId,
  sowId,
  accessToken,
  isConsultant = false,
  currentUser,
  configItemCategory,
  consultingFirmId,
}: DiscoveryQuestionsPanelProps) {
  const [answers, setAnswers] = useState<QuestionAnswer[]>([]);
  const [completeness, setCompleteness] = useState({
    totalQuestions: 0,
    answeredQuestions: 0,
    completenessPercentage: 0,
  });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [skippedQuestions, setSkippedQuestions] = useState(0);

  // AI question skipping: what the engagement's documents already answer for this CI
  const skipPlan = useSkipPlan({ companyId, accessToken, scope: { kind: 'item', itemId: configItemId }, engagementId: sowId ?? null });
  const [wizardChoiceDialogOpen, setWizardChoiceDialogOpen] = useState(false);
  const [selectedWizardId, setSelectedWizardId] = useState<string | null>(null);
  const [autoLaunchWizards, setAutoLaunchWizards] = useState<any[]>([]);
  const [hasCheckedAutoLaunch, setHasCheckedAutoLaunch] = useState(false);
  
  // Phase 4: Smart Grid state
  const [questionMetadata, setQuestionMetadata] = useState<Record<string, Question>>({});
  const [wizardSessions, setWizardSessions] = useState<Record<string, any>>({});
  const [groupedQuestions, setGroupedQuestions] = useState<{ wizardGroups: WizardGroup[], standaloneQuestions: QuestionAnswer[] } | null>(null);
  const [highlightedWizard, setHighlightedWizard] = useState<string | null>(null);

  useEffect(() => {
    loadQuestions();
  }, [configItemId]);

  // Check for auto-launch on first view
  useEffect(() => {
    if (!hasCheckedAutoLaunch && configItemCategory && consultingFirmId && completeness.answeredQuestions === 0) {
      checkAutoLaunch();
      setHasCheckedAutoLaunch(true);
    }
  }, [configItemCategory, consultingFirmId, completeness, hasCheckedAutoLaunch]);

  const checkAutoLaunch = async () => {
    if (!configItemCategory || !consultingFirmId) return;

    try {
      const result = await checkWizardAutoLaunch(
        configItemCategory,
        'on_first_view',
        isConsultant ? 'consultant' : 'client',
        consultingFirmId
      );

      if (result.shouldAutoLaunch) {
        setAutoLaunchWizards(result.wizards);
        
        if (result.wizards.length === 1) {
          // Only one wizard - auto-launch it
          setSelectedWizardId(result.wizards[0].id);
          setWizardOpen(true);
        } else if (result.multipleOptions) {
          // Multiple wizards - show choice dialog
          setWizardChoiceDialogOpen(true);
        }
      }
    } catch (error) {
      console.error('Error checking auto-launch:', error);
    }
  };

  // Check for wizard session and skipped questions
  useEffect(() => {
    checkWizardSession();
  }, [configItemId, completeness]);

  const checkWizardSession = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/discovery/${companyId}/${configItemId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        const { session } = await response.json();
        if (session && session.wizardUsed && session.questionsSkipped) {
          setSkippedQuestions(session.questionsSkipped.length || 0);
        }
      }
    } catch (error) {
      console.error('Error checking wizard session:', error);
    }
  };

  const loadQuestions = async () => {
    try {
      setLoading(true);

      // Load answered questions
      const answersData = await getAnsweredQuestions(
        configItemId,
        companyId,
        accessToken
      );
      setAnswers(answersData);

      // Load completeness metrics
      const completenessData = await getConfigItemCompleteness(
        configItemId,
        companyId,
        accessToken
      );
      setCompleteness({
        totalQuestions: completenessData.totalQuestions,
        answeredQuestions: completenessData.answeredQuestions,
        completenessPercentage: completenessData.completenessPercentage,
      });

      // Phase 4: Load question metadata and wizard sessions
      if (consultingFirmId) {
        const metadata = await fetchQuestionMetadata(consultingFirmId, accessToken);
        setQuestionMetadata(metadata);

        const sessions = await fetchWizardSessions(configItemId, companyId, accessToken);
        setWizardSessions(sessions);

        // Group questions by wizard
        const grouped = groupQuestionsByWizard(answersData, metadata, sessions);
        setGroupedQuestions(grouped);
      }
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load discovery questions');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadQuestions();
    
    // Also recalculate SOW completeness if we have a sowId
    if (sowId) {
      try {
        const sowCompleteness = await recalculateSOWCompleteness(sowId, accessToken);
        toast.success(`SOW progress updated: ${sowCompleteness.progressPercentage}%`);
      } catch (error) {
        console.error('Error recalculating SOW completeness:', error);
      }
    }
    
    setRefreshing(false);
  };

  const handleAnswerUpdated = async (questionId: string, newAnswer: string) => {
    // Update local state
    setAnswers(prevAnswers =>
      prevAnswers.map(ans =>
        ans.questionId === questionId
          ? { ...ans, answer: newAnswer, updatedAt: new Date().toISOString() }
          : ans
      )
    );

    // Refresh completeness
    await handleRefresh();
  };

  const handleStartWizard = () => {
    // Check if we should show multiple wizard options
    if (autoLaunchWizards.length > 1) {
      setWizardChoiceDialogOpen(true);
    } else if (autoLaunchWizards.length === 1) {
      setSelectedWizardId(autoLaunchWizards[0].id);
      setWizardOpen(true);
    } else {
      // No auto-launch wizards detected, check again
      checkAutoLaunch();
    }
  };

  const handleWizardComplete = async () => {
    setWizardOpen(false);
    await loadQuestions();
    await checkWizardSession();
    toast.success('Your answers have been saved.');
  };

  // Determine if wizard is available for this category
  const hasWizard = (category?: string): boolean => {
    if (!category) return false;
    const wizardCategories = [
      'assets',
      'incidents',
      'service_requests',
      'problems',
      'changes',
      'slas',
      'queues',
      'automation',
      'permissions',
      'integrations',
      'workflows',
      'knowledge',
      'portal',
      'reporting',
      'forms'
    ];
    return wizardCategories.includes(category);
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <RefreshCw className="w-5 h-5 animate-spin mr-2" />
            <span>Loading discovery questions...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Discovery Questions</CardTitle>
            <CardDescription>
              {configItemName} - Track progress toward grid completion
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* AI pre-fill from the engagement's documents (skip plan) */}
        {skipPlan.plan && (
          <SkipPlanBanner
            plan={skipPlan.plan}
            questionText={Object.fromEntries((skipPlan.plan.questions ?? []).map((q) => [q.id, q.text]))}
            applying={skipPlan.applying}
            onAccept={async (accepted) => {
              const ok = await skipPlan.apply(accepted);
              if (ok) {
                toast.success(`${accepted.length} ${accepted.length === 1 ? 'answer' : 'answers'} applied from your documents`);
                await loadQuestions();
              } else {
                toast.error('Could not apply the suggested answers');
              }
              return ok;
            }}
            className="mb-4"
          />
        )}

        {/* Wizard Banner - Shows at top based on state */}
        {hasWizard(configItemCategory) && (
          <WizardBanner
            configItemId={configItemId}
            configItemName={configItemName}
            totalQuestions={completeness.totalQuestions}
            answeredQuestions={completeness.answeredQuestions}
            skippedQuestions={skippedQuestions}
            hasWizard={true}
            onStartWizard={handleStartWizard}
            onContinueWizard={handleStartWizard}
          />
        )}

        {/* Completeness Progress */}
        <div className="p-4 bg-blue-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              <span className="font-medium text-blue-900">Discovery Progress</span>
            </div>
            <Badge variant={completeness.completenessPercentage === 100 ? 'default' : 'secondary'}>
              {completeness.answeredQuestions} / {completeness.totalQuestions} Questions
            </Badge>
          </div>
          <Progress value={completeness.completenessPercentage} className="h-2 mb-2" />
          <p className="text-sm text-blue-700">
            {completeness.completenessPercentage}% complete - 
            {completeness.completenessPercentage === 100 
              ? ' Ready for grid' 
              : ` ${completeness.totalQuestions - completeness.answeredQuestions} questions remaining`}
          </p>
        </div>

        {/* Questions List */}
        <div className="space-y-4">
          <h3 className="font-semibold text-sm text-gray-700">Answered Questions</h3>
          
          {answers.length === 0 ? (
            <div className="text-center py-8 border-2 border-dashed border-gray-300 rounded-lg">
              <Circle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-500">No questions answered yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Use the guided wizard to start answering discovery questions
              </p>
            </div>
          ) : groupedQuestions ? (
            <div className="space-y-6">
              {/* Wizard Question Groups */}
              {groupedQuestions.wizardGroups.map((wizardGroup) => (
                <WizardQuestionGroup
                  key={wizardGroup.wizardId}
                  wizardId={wizardGroup.wizardId}
                  wizardName={wizardGroup.wizardName}
                  questions={wizardGroup.questions}
                  totalPages={wizardGroup.totalPages}
                  sessionExists={wizardGroup.sessionExists}
                  sessionComplete={wizardGroup.sessionComplete}
                  currentPage={wizardGroup.currentPage}
                  onOpenWizard={(wizardId) => {
                    setSelectedWizardId(wizardId);
                    setWizardOpen(true);
                  }}
                  onViewAnswers={(wizardId) => {
                    setSelectedWizardId(wizardId);
                    setWizardOpen(true);
                  }}
                  isHighlighted={highlightedWizard === wizardGroup.wizardId}
                  onMouseEnter={() => setHighlightedWizard(wizardGroup.wizardId)}
                  onMouseLeave={() => setHighlightedWizard(null)}
                >
                  {/* Render question cards inside wizard group */}
                  {wizardGroup.questions.map((wq) => (
                    <QuestionCard
                      key={wq.questionId}
                      question={{
                        questionId: wq.questionId,
                        questionText: wq.questionText,
                        answer: wq.answer,
                        answered: wq.answered,
                        required: wq.required,
                        gridBehavior: wq.gridBehavior
                      }}
                      configItemId={configItemId}
                      companyId={companyId}
                      accessToken={accessToken}
                      isConsultant={isConsultant}
                      canEditInline={wq.gridBehavior !== 'wizard_only'}
                      onAnswerUpdated={handleAnswerUpdated}
                      isHighlighted={highlightedWizard === wizardGroup.wizardId}
                      onOpenWizard={() => {
                        setSelectedWizardId(wizardGroup.wizardId);
                        setWizardOpen(true);
                      }}
                    />
                  ))}
                </WizardQuestionGroup>
              ))}

              {/* Standalone Questions (not part of any wizard) */}
              {groupedQuestions.standaloneQuestions.length > 0 && (
                <div className="space-y-3">
                  <h4 className="font-medium text-sm text-gray-700 border-b pb-2">
                    Other Questions
                  </h4>
                  {groupedQuestions.standaloneQuestions.map((qa) => (
                    <QuestionCard
                      key={qa.questionId}
                      question={{
                        questionId: qa.questionId,
                        questionText: qa.questionText,
                        answer: qa.answer,
                        answered: true,
                        confidence: qa.confidence,
                        answeredAt: qa.answeredAt,
                        source: qa.source
                      }}
                      configItemId={configItemId}
                      companyId={companyId}
                      accessToken={accessToken}
                      isConsultant={isConsultant}
                      canEditInline={true}
                      onAnswerUpdated={handleAnswerUpdated}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Fallback to original flat list if grouping fails
            <div className="space-y-4">
              {answers.map((qa) => (
                <QuestionCard
                  key={qa.questionId}
                  question={{
                    questionId: qa.questionId,
                    questionText: qa.questionText,
                    answer: qa.answer,
                    answered: true,
                    confidence: qa.confidence,
                    answeredAt: qa.answeredAt,
                    source: qa.source
                  }}
                  configItemId={configItemId}
                  companyId={companyId}
                  accessToken={accessToken}
                  isConsultant={isConsultant}
                  canEditInline={true}
                  onAnswerUpdated={handleAnswerUpdated}
                />
              ))}
            </div>
          )}
        </div>

        {/* Grid Impact Notice */}
        {completeness.completenessPercentage === 100 && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-green-600" />
              <div>
                <p className="font-medium text-green-900">
                  All questions answered
                </p>
                <p className="text-sm text-green-700 mt-1">
                  This config item will now show as complete in the discovery grid and contribute to overall SOW progress metrics.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Wizard Choice Dialog */}
        <WizardChoiceDialog
          open={wizardChoiceDialogOpen}
          onClose={() => setWizardChoiceDialogOpen(false)}
          wizards={autoLaunchWizards}
          onSelectWizard={(wizardId) => {
            setSelectedWizardId(wizardId);
            setWizardChoiceDialogOpen(false);
            setWizardOpen(true);
          }}
          configItemName={configItemName}
        />

        {/* Dynamic Wizard */}
        {selectedWizardId && consultingFirmId && (
          <DynamicWizard
            open={wizardOpen}
            onClose={() => {
              setWizardOpen(false);
              setSelectedWizardId(null);
            }}
            wizardId={selectedWizardId}
            configItemId={configItemId}
            companyId={companyId}
            sowId={sowId}
            consultingFirmId={consultingFirmId}
            onComplete={handleWizardComplete}
            userRole={isConsultant ? 'consultant' : 'client'}
          />
        )}
      </CardContent>
    </Card>
  );
}